import React from 'react';

import {LocalizedHtml, useI18n} from '@udemy/i18n';
import {Modal} from '@udemy/react-dialog-components';

import styles from './how-assessments-work-modal.module.less';

const CDN_BASE = 'https://frontends.udemycdn.com/frontends-pro-assessments';

const PROFICIENCY_LEVELS = [
    {id: 'beginning', minScore: 0, maxScore: 49},
    {id: 'developing', minScore: 50, maxScore: 99},
    {id: 'established', minScore: 100, maxScore: 149},
    {id: 'advanced', minScore: 150, maxScore: 200},
];

export interface HowAssessmentsWorkModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const HowAssessmentsWorkModal: React.FC<HowAssessmentsWorkModalProps> = ({
    isOpen,
    onClose,
}) => {
    const {gettext} = useI18n();

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={gettext('How assessments work')}>
            <ul className={`ud-text-md ${styles.rules}`}>
                <li>
                    {gettext(
                        'Each assessment includes around 30 questions and takes 25–35 minutes to complete.',
                    )}
                </li>
                <li>{gettext('Questions are in multiple choice or multi-select format.')}</li>
                <li>
                    <LocalizedHtml
                        html={gettext(
                            'Each assessment allows up to <strong class="bold">2 attempts within 24 hours</strong>.',
                        )}
                        interpolate={{bold: <strong className="ud-heading-md" />}}
                    />
                </li>
                <li>
                    {gettext("You may skip a question, but skipped questions can't be revisited.")}
                </li>
                <li>
                    <LocalizedHtml
                        html={gettext(
                            'Your progress will be saved if you exit during the assessment. You can <strong class="bold">resume within 24 hours</strong>; otherwise, you\'ll need to start a new attempt.',
                        )}
                        interpolate={{bold: <strong className="ud-heading-md" />}}
                    />
                </li>
                <li>
                    {gettext(
                        "You'll receive your final score at the end, along with recommended content to help you improve.",
                    )}
                </li>
            </ul>

            <div className={styles['proficiency-levels']}>
                {PROFICIENCY_LEVELS.map((level) => (
                    <div key={level.id} className={styles['proficiency-level']}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={`${CDN_BASE}/images/proficiency-badges/rank-badge-${level.id}-v1.svg`}
                            alt=""
                            aria-hidden
                            width={32}
                            height={33}
                        />
                        <span className="ud-heading-sm">
                            {level.id === 'beginning' && gettext('Foundational')}
                            {level.id === 'developing' && gettext('Intermediate')}
                            {level.id === 'established' && gettext('Established')}
                            {level.id === 'advanced' && gettext('Advanced')}
                        </span>
                        <p className={`ud-text-xs ${styles['level-range']}`}>
                            {level.minScore}–{level.maxScore}
                        </p>
                    </div>
                ))}
            </div>
        </Modal>
    );
};
